import { useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ShoppingCart,
  CaretLeft,
  Circle,
  X,
} from "@phosphor-icons/react";
import { HashLoader } from "react-spinners";
import { Field, Form, Formik } from "formik";
import * as yup from "yup";
import moment from "moment";
import { toast } from "sonner";
import {
  CheckoutHeldDetails,
  getCheckoutHeldDetails,
  useCompleteCartPayment,
} from "../../../hooks/useCompleteCartPayment";
import useSelectPaymentOption, {
  PaymentData,
} from "../../../hooks/useSelectPaymentOptionForCheckout";
import { useAuth } from "../../../context/AuthContext";
import { formatAsMoney } from "../../../helpers/numbers";
import colors from "../../../assets/colors";
import PaymentHeldOverlay from "./PaymentHeldOverlay";

const toNumber = (value: unknown): number =>
  parseFloat(String(value ?? "0").replace(/,/g, "")) || 0;

const SCHEMA = yup.object().shape({
  accepted: yup.bool().isTrue("You have to accept the terms and conditions"),
});

const PayLater = () => {
  const { state: params } = useLocation();

  const { orderId } = useParams();

  const { user, refetch } = useAuth();

  const navigate = useNavigate();

  const [confirming, setConfirming] = useState(false);
  const [bookingError, setOrderError] = useState(false);
  const [heldDetails, setHeldDetails] = useState<CheckoutHeldDetails | null>(
    null
  );
  // Survives re-renders without one, so a double click can't submit twice.
  const submittingRef = useRef(false);

  const {
    completeCartPayment,
    completeCartPaymentError,
    completingCartPayment,
    isCompleteCartPaymentError,
  } = useCompleteCartPayment({
    onError(error) {
      refetch();
      const held = getCheckoutHeldDetails(error);
      if (held) {
        setHeldDetails(held);
        return;
      }
      setOrderError(true);
      toast.error(error.message);
    },
    onSettled() {
      submittingRef.current = false;
    },
    onSuccess(data) {
      refetch();
      toast.success(data.message);
      // Go to a dedicated success route rather than a transient in-page overlay
      // (the overlay could be lost to a re-render/remount, leaving the user stuck).
      navigate("/marketplace/order-success");
    },
  });

  // The plan shown here (down payment, installments, loan_id) is the result of
  // the payment-option selection. It can be recomputed when the customer chooses
  // a higher down payment (aviation parity — pay more to reduce the loan).
  const [plan, setPlan] = useState<PaymentData | undefined>(
    params?.paymentData?.payment_data
  );
  const optionCode: string = params?.paymentData?.option?.code;

  const orderTotal = toNumber(plan?.amount);
  const minDownPayment = useMemo(
    () =>
      plan?.minimum_down_payment ??
      toNumber(plan?.formatted?.minimum_down_payment) ??
      toNumber(plan?.formatted?.down_payment),
    [plan]
  );
  const currentDownPayment =
    plan?.down_payment ?? toNumber(plan?.formatted?.down_payment);
  const commissionFee = toNumber(plan?.commission_fee);
  const walletBalance = toNumber(user?.wallet?.available_balance);
  const totalDueNow = currentDownPayment + commissionFee;

  const [downPaymentInput, setDownPaymentInput] = useState<string>(
    String(currentDownPayment || minDownPayment || "")
  );

  const { selectPaymentOption, selectingPaymentOption } = useSelectPaymentOption({
    onSuccess(data) {
      const next = data?.data?.original?.data?.payment_data as
        | PaymentData
        | undefined;
      if (!next) return;
      setPlan(next);
      if (next.full_payoff) {
        toast.info(
          "Your down payment covers the full order — there's nothing to finance. Go back and choose Pay Now to pay in full."
        );
      } else {
        setDownPaymentInput(
          String(next.down_payment ?? toNumber(next.formatted?.down_payment))
        );
        toast.success("Payment plan updated.");
      }
    },
    onError(error) {
      toast.error(error.message);
    },
  });

  const recomputePlan = () => {
    const amount = toNumber(downPaymentInput);
    if (amount < minDownPayment) {
      toast.error(
        `Down payment must be at least ₦${formatAsMoney(minDownPayment)}.`
      );
      return;
    }
    if (amount > orderTotal) {
      toast.error("Down payment cannot exceed the order total.");
      return;
    }
    selectPaymentOption({
      session_id: orderId,
      payment_option_id: optionCode,
      down_payment_amount: amount,
      loan_id: plan?.loan_id,
    });
  };

  const isFullPayoff = Boolean(plan?.full_payoff);

  return (
    <div>
      {(completingCartPayment || selectingPaymentOption) && (
        <div className="fixed top-0 z-[99] left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}
      {confirming && (
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{
            type: "tween",
            ease: "anticipate",
          }}
          className="flex items-center justify-center gap-10 py-20 text-center flex-col bg-white absolute top-0 left-0 w-full z-50 h-full"
        >
          <div className="max-w-2xl flex flex-col items-center justify-center gap-8">
            <div className="bg-visaro-primary-700 rounded-full p-8 text-white">
              <ShoppingCart size={36} />
            </div>
            <div className="text-center flex flex-col gap-2">
              <div className="text-3xl font-medium">Are you sure?</div>
              <div className="text-lg">
                You are about to pay{" "}
                <b className="font-semibold">
                  ₦{" "}
                  {formatAsMoney(totalDueNow)}{" "}
                </b>
                from your Visaro wallet: ₦{formatAsMoney(currentDownPayment)}
                {" "}down payment plus ₦{formatAsMoney(commissionFee)} service fee.
                If the loan is approved, the required lien is handled securely in
                the background. If it is declined, your down payment is refunded.
              </div>
            </div>
            <div className="flex justify-between w-full">
              <button
                onClick={() => setConfirming(false)}
                type="button"
                className="p-2 py-3 text-center bg-red-100 text-red-500 px-6 ring-2 ring-red-100/50 rounded-xl font-medium flex gap-2 items-center"
              >
                <span>Cancel</span>
              </button>
              <button
                onClick={() => {
                  if (submittingRef.current) return;
                  submittingRef.current = true;

                  setConfirming(false);
                  completeCartPayment({
                    payment_option_id: optionCode,
                    loan_id: plan?.loan_id ?? "",
                    session_id: orderId!!,
                    commission_source: "wallet",
                  });
                }}
                className="p-2 py-3 text-center bg-deepBlue text-white px-10 ring-2 ring-deepBlue/50 rounded-xl font-medium"
              >
                Pay
              </button>
            </div>
          </div>
        </motion.div>
      )}
      {heldDetails && (
        <PaymentHeldOverlay
          details={heldDetails}
          message={completeCartPaymentError?.message}
          onViewOrders={() => navigate("/payments/orders")}
        />
      )}
      {isCompleteCartPaymentError && bookingError && (
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{
            type: "tween",
            ease: "anticipate",
          }}
          className="flex items-center justify-center gap-10 py-20 text-center flex-col bg-white absolute top-0 left-0 w-full z-50 h-full"
        >
          <div className="max-w-2xl flex flex-col items-center justify-center gap-8">
            <div className="bg-red-500 rounded-full p-8 text-white">
              <X size={36} />
            </div>
            <div className="flex flex-col items-center gap-2 w-full">
              <div className="text-3xl font-medium">Order Failed!</div>
              <div className="w-full line-clamp-6">
                {completeCartPaymentError?.message.toString()}
              </div>
            </div>
            <button
              onClick={() => setOrderError(false)}
              type="button"
              className="p-2 py-3 text-center bg-red-100 text-red-500 px-6 ring-2 ring-red-100/50 rounded-xl font-medium flex gap-2 items-center"
            >
              <span>Cancel</span>
            </button>
          </div>
        </motion.div>
      )}
      <Formik
        validationSchema={SCHEMA}
        initialValues={{
          accepted: false,
        }}
        onSubmit={() => {
          if (isFullPayoff) {
            toast.error(
              "Your down payment covers the full order. Please go back and choose Pay Now to pay in full."
            );
            return;
          }

          const downPayment = currentDownPayment;

          // Wallet existence check
          if (!user?.wallet) {
            toast.error("You need an active Visaro wallet to pay in installments.");
            return;
          }
          // Balance sufficiency check
          if (walletBalance < totalDueNow) {
            toast.error(
              `Insufficient wallet balance. You need ₦${formatAsMoney(totalDueNow)} for the ₦${formatAsMoney(downPayment)} down payment and ₦${formatAsMoney(commissionFee)} service fee, but have ₦${formatAsMoney(walletBalance)}. Please fund your wallet.`
            );
            return;
          }

          setConfirming(true);
          window.scrollTo({
            top: 0,
            left: 0,
          });
        }}
      >
        {({ isValid, dirty }) => {
          return (
            <Form className="relative w-full flex items-center justify-center gap-10 px-5 py-20 flex-col">
              <div className="flex flex-col gap-5 max-w-4xl w-full items-start">
                <div className="font-semibold text-3xl">Pay Later (BNPL)</div>
                <div className="text-gray-500">You'll pay from your Visaro wallet</div>
                <div className="text-3xl font-semibold">
                  ₦ {formatAsMoney(plan?.formatted?.down_payment || 0)}
                </div>

                {/* Flexible down payment — the % shown is the minimum; paying more
                    upfront reduces the financed amount. */}
                <div className="flex flex-col gap-2 w-full max-w-md">
                  <label className="text-gray-500 font-medium">
                    Down payment
                  </label>
                  <div className="flex items-stretch gap-2">
                    <div className="flex items-center gap-1 border border-gray-300 rounded-xl px-3 flex-1 focus-within:ring-2 focus-within:ring-deepBlue/40">
                      <span className="text-gray-500">₦</span>
                      <input
                        type="number"
                        inputMode="decimal"
                        min={minDownPayment}
                        max={orderTotal}
                        value={downPaymentInput}
                        onChange={(e) => setDownPaymentInput(e.target.value)}
                        className="w-full py-3 outline-none bg-transparent"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={recomputePlan}
                      disabled={
                        selectingPaymentOption ||
                        toNumber(downPaymentInput) === currentDownPayment
                      }
                      className="px-5 rounded-xl bg-deepBlue text-white font-medium disabled:opacity-40"
                    >
                      Update
                    </button>
                  </div>
                  <div className="text-sm text-gray-500">
                    Minimum ₦{formatAsMoney(minDownPayment)}
                    {plan?.down_payment_percent
                      ? ` (${plan.down_payment_percent}% of order)`
                      : ""}
                    . Pay more upfront to reduce your loan.
                  </div>
                </div>

                {isFullPayoff ? (
                  <div className="w-full max-w-md rounded-xl border border-amber-200 bg-amber-50 text-amber-800 px-4 py-3 text-sm">
                    Your down payment covers the full order — there's nothing to
                    finance. Go back and choose{" "}
                    <b>Pay Now</b> to complete this order in full.
                  </div>
                ) : (
                  <div>
                    <b>
                      ₦ {formatAsMoney(plan?.total_amount_payable || 0)}{" "}
                    </b>
                    total payable, including your upfront payment. The remaining principal and interest are paid in two monthly installments of about{" "}
                    <b>
                      ₦{" "}
                      {formatAsMoney(plan?.single_installment_amount || 0)}{" "}
                    </b>
                    . Final dates and amounts are confirmed after Alert approves your loan.
                  </div>
                )}
                <div className="flex flex-col gap-5">
                  <div className="text-gray-500 font-medium">
                    Schedule of payments
                  </div>
                  <div>
                    {plan?.instalments &&
                      plan?.instalments?.map(
                        (item, index: number) => {
                          return (
                            <div
                              key={index}
                              className="flex items-start relative"
                            >
                              <div className="border border-gray-200 relative z-10 mr-4 rounded-full flex flex-col items-center">
                                <Circle
                                  name="circle"
                                  weight="fill"
                                  color={
                                    item.due === "today"
                                      ? colors.ORANGE_01
                                      : colors.GREY_05
                                  }
                                  size={20}
                                />
                              </div>
                              {index <
                                (plan?.instalments?.length ?? 0) - 1 && (
                                <div className="h-full w-[2px] bg-gray-100 absolute top-4 z-0 left-2.5 transform -translate-x-1/2" />
                              )}
                              <div className="pb-4">
                                <div className="text-lg font-medium">
                                  ₦ {formatAsMoney(item.amount)}
                                </div>
                                <div>
                                  Due:{" "}
                                  {item.due === "today"
                                    ? "Today"
                                    : moment(item.due).format("ll")}
                                </div>
                              </div>
                            </div>
                          );
                        }
                      )}
                  </div>
                </div>

                <div className="flex flex-row gap-10">
                  <div
                    style={{
                      gap: 2,
                      flex: 1,
                    }}
                  >
                    <div className="font-medium">Transaction Cost</div>
                    <div className="text-lg">
                      ₦{plan?.formatted?.processing_fee}{" "}
                    </div>
                  </div>
                  <div
                    style={{
                      gap: 2,
                      flex: 1,
                    }}
                  >
                    <div className="font-medium">Monthly interest rate</div>
                    <div className="text-lg">{plan?.interest_rate != null ? `${plan.interest_rate}%` : "Confirmed with your loan"}</div>
                    <div className="text-sm text-gray-500">Applied to the full order price for {plan?.interest_months ?? 3} months.</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Field
                    type="checkbox"
                    name="accepted"
                    className="w-4 aspect-square accent-deepBlue"
                  />{" "}
                  <span>
                    By clicking ‘Continue’ , you agree to{" "}
                    <Link
                      to=""
                      className="text-visaro-orange font-medium underline"
                    >
                      Terms and Condition
                    </Link>{" "}
                    &{" "}
                    <Link
                      to=""
                      className="text-visaro-orange font-medium underline"
                    >
                      Privacy Policy
                    </Link>
                  </span>
                </div>
              </div>
              <div className="flex justify-between w-full max-w-4xl">
                <button
                  onClick={() => navigate(-1)}
                  type="button"
                  className="p-2 py-3 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300"
                >
                  <CaretLeft />
                  <span>Back</span>
                </button>
                <button
                  disabled={!isValid || !dirty || isFullPayoff || selectingPaymentOption}
                  type="submit"
                  className="p-2 py-3 text-center bg-deepBlue text-white px-10 ring-2 ring-deepBlue/50 rounded-xl font-medium disabled:opacity-40"
                >
                  Pay
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default PayLater;
